import { FC, useState, useEffect, useMemo, useCallback } from 'react';
import { useEndpoints } from '../../../hooks/useEndpoints';
import { useAuth } from '../../../contexts/AuthContext';
import {
  IWorkout,
  IWorkoutAssignment,
  IWorkoutLog,
  IExerciseLog,
  ISetLog,
  IExerciseInBlock,
  IBlock,
} from '../../../models/programming';
import {
  BLOCK_TYPE_COLORS,
  BLOCK_TYPE_LABELS,
  EXERCISE_LOG_STATUS,
  EXERCISE_METHOD,
  EXERCISE_METHOD_LABELS,
  LOAD_UNIT,
  LOAD_UNIT_LABELS,
  WORKOUT_LOG_STATUS,
} from '../../../enums/programming';

// --- Types ---

interface SetFormData {
  setNumber: number;
  repsActual: number;
  weight: number;
  unit: string;
  rpe: number | null;
  laterality?: string;
}

interface ExerciseFormData {
  exerciseId: string;
  exerciseName: string;
  method: EXERCISE_METHOD;
  blockType: string;
  prescribedSets: number;
  prescribedReps: string;
  prescribedLoads: { value: number; unit: LOAD_UNIT }[];
  isUnilateral: boolean;
  sets: SetFormData[];
  notes: string;
}

interface CoachCompileModalProps {
  open: boolean;
  workout: IWorkout;
  assignment: IWorkoutAssignment;
  athleteId: string;
  athleteName: string;
  dayNumber?: number;
  existingLog?: IWorkoutLog | null;
  onClose: () => void;
  onSaved?: (log: IWorkoutLog) => void;
}

// --- Helpers ---

const buildInitialSets = (exercise: IExerciseInBlock): SetFormData[] => {
  const numSets = exercise.sets || 1;
  const defaultWeight = exercise.loads?.[0]?.value ?? 0;
  const defaultUnit = exercise.loads?.[0]?.unit ?? LOAD_UNIT.KG;
  const defaultReps = parseInt(exercise.reps, 10) || 0;

  if (exercise.isUnilateral) {
    const sets: SetFormData[] = [];
    for (let i = 1; i <= numSets; i++) {
      sets.push({ setNumber: i, repsActual: defaultReps, weight: defaultWeight, unit: defaultUnit, rpe: null, laterality: 'dx' });
      sets.push({ setNumber: i, repsActual: defaultReps, weight: defaultWeight, unit: defaultUnit, rpe: null, laterality: 'sx' });
    }
    return sets;
  }

  return Array.from({ length: numSets }, (_, i) => ({
    setNumber: i + 1,
    repsActual: defaultReps,
    weight: defaultWeight,
    unit: defaultUnit,
    rpe: null,
  }));
};

const buildExerciseFormData = (blocks: IBlock[]): ExerciseFormData[] => {
  const exercises: ExerciseFormData[] = [];
  for (const block of blocks) {
    for (const ex of block.exercises || []) {
      const name = ex.exercise?.name?.it || ex.exercise?.name?.en || ex.exerciseId.slice(-6);
      exercises.push({
        exerciseId: ex.exerciseId,
        exerciseName: name,
        method: ex.method || EXERCISE_METHOD.STANDARD,
        blockType: block.type,
        prescribedSets: ex.sets || 1,
        prescribedReps: ex.reps || '—',
        prescribedLoads: ex.loads || [],
        isUnilateral: ex.isUnilateral || false,
        sets: buildInitialSets(ex),
        notes: '',
      });
    }
  }
  return exercises;
};

const hydrateFromLog = (exercises: ExerciseFormData[], log: IWorkoutLog): ExerciseFormData[] => {
  return exercises.map((ex) => {
    const exLog = log.exercises?.find((l) => l.exerciseId === ex.exerciseId);
    if (!exLog?.sets?.length) return ex;
    return {
      ...ex,
      notes: exLog.notes || '',
      sets: exLog.sets.map((s) => ({
        setNumber: s.setNumber,
        repsActual: s.repsActual ?? 0,
        weight: s.weight ?? s.loadActual ?? 0,
        unit: s.unit ?? s.loadActualUnit ?? ex.prescribedLoads[0]?.unit ?? LOAD_UNIT.KG,
        rpe: s.rpe ?? null,
        laterality: s.laterality,
      })),
    };
  });
};

const formatPrescription = (ex: ExerciseFormData): string => {
  const loadStr = ex.prescribedLoads.map((l) => `${l.value}${LOAD_UNIT_LABELS[l.unit] || l.unit}`).join(' / ');
  return `${ex.prescribedSets}×${ex.prescribedReps}${loadStr ? ` @ ${loadStr}` : ''}`;
};

// --- Component ---

export const CoachCompileModal: FC<CoachCompileModalProps> = ({
  open,
  workout,
  assignment,
  athleteId,
  athleteName,
  dayNumber = 1,
  existingLog,
  onClose,
  onSaved,
}) => {
  const { postWorkoutLog, patchWorkoutLog, completeWorkoutLog } = useEndpoints();
  const { user } = useAuth();

  const coachId = user?.id || '';

  const [exercises, setExercises] = useState<ExerciseFormData[]>([]);
  const [globalRpe, setGlobalRpe] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedExercise, setExpandedExercise] = useState<string | null>(null);

  // Build form data from workout structure
  const currentDay = useMemo(() => {
    return workout.days?.find((d) => d.dayNumber === dayNumber) || workout.days?.[0];
  }, [workout, dayNumber]);

  useEffect(() => {
    if (!currentDay) return;
    let data = buildExerciseFormData(currentDay.blocks || []);
    if (existingLog) {
      data = hydrateFromLog(data, existingLog);
      setGlobalRpe(existingLog.rpe ?? null);
    }
    setExercises(data);
    if (data.length > 0) setExpandedExercise(data[0].exerciseId);
  }, [currentDay, existingLog]);

  // --- Handlers ---

  const updateSet = useCallback((exerciseId: string, setIdx: number, field: keyof SetFormData, value: number | string | null) => {
    setExercises((prev) =>
      prev.map((ex) => {
        if (ex.exerciseId !== exerciseId) return ex;
        const newSets = [...ex.sets];
        newSets[setIdx] = { ...newSets[setIdx], [field]: value };
        return { ...ex, sets: newSets };
      }),
    );
  }, []);

  const updateNotes = useCallback((exerciseId: string, notes: string) => {
    setExercises((prev) =>
      prev.map((ex) => (ex.exerciseId === exerciseId ? { ...ex, notes } : ex)),
    );
  }, []);

  const handleSave = useCallback(async () => {
    if (!assignment?._id || !coachId) return;
    setSaving(true);
    setError(null);

    try {
      // Build exercise logs.
      // FIX: il carico inserito dal coach va in `loadActual` (campo effettivo letto
      // dalla UI coach), non solo in `weight` (prescritto) → altrimenti il coach
      // non rivede i dati che ha inserito.
      const exerciseLogs: IExerciseLog[] = exercises.map((ex) => ({
        exerciseId: ex.exerciseId,
        blockType: ex.blockType,
        status: EXERCISE_LOG_STATUS.COMPLETED,
        sets: ex.sets.map((s): ISetLog => ({
          setNumber: s.setNumber,
          repsActual: s.repsActual,
          loadActual: s.weight,
          loadActualUnit: s.unit,
          weight: s.weight,
          unit: s.unit,
          rpe: s.rpe ?? undefined,
          laterality: s.laterality,
          compiledByCoach: true,
        })),
        notes: ex.notes || undefined,
      }));

      // FIX root cause: la PATCH /logs/:id accetta SOLO `exercises` (UpdateWorkoutLogDto
      // + whitelist:true scarta status/rpe/compiledBy). status=COMPLETED, compiledIds
      // sull'evento e sRPE vengono scritti SOLO da completeWorkoutLog. Senza chiamarlo
      // il log resta in_progress → OccurrenceDetailModal lo vede "Non completato" e
      // nasconde l'espansione per-serie. Quindi: PATCH exercises → POST /complete.
      let logId: string;
      if (existingLog?._id) {
        logId = existingLog._id;
        await patchWorkoutLog(logId, { exercises: exerciseLogs });
      } else {
        const createRes = await postWorkoutLog({
          workoutAssignmentId: assignment._id,
          athleteId,
          dayNumber,
          date: new Date().toISOString(),
        });
        logId = createRes.data._id;
        await patchWorkoutLog(logId, { exercises: exerciseLogs });
      }
      // Completa: status→COMPLETED + compiledIds + sRPE (Strain Recovery).
      const completeRes = await completeWorkoutLog(logId, { rpe: globalRpe ?? 0 });

      onSaved?.(completeRes.data);
      onClose();
    } catch (err: any) {
      console.error('Error saving coach compile:', err);
      setError(err?.response?.data?.message || err?.message || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  }, [exercises, globalRpe, existingLog, assignment, athleteId, dayNumber, coachId, onClose, onSaved]);

  // --- Render ---

  if (!open) return null;

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={headerStyle}>
          <div>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#1A202C' }}>
              Compila per {athleteName}
            </h2>
            <div style={{ fontSize: 13, color: '#718096', marginTop: 4 }}>
              {workout.title} — Giorno {dayNumber}
            </div>
          </div>
          <button onClick={onClose} style={closeButtonStyle} title="Chiudi">
            ×
          </button>
        </div>

        {/* Body */}
        <div style={bodyStyle}>
          {exercises.length === 0 ? (
            <div style={{ padding: 32, textAlign: 'center', color: '#A0AEC0' }}>
              Nessun esercizio per questo giorno.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {exercises.map((ex) => {
                const isExpanded = expandedExercise === ex.exerciseId;
                const blockColor = BLOCK_TYPE_COLORS[ex.blockType as keyof typeof BLOCK_TYPE_COLORS] || '#718096';

                return (
                  <div key={ex.exerciseId} style={{ border: '1px solid #E2E8F0', borderRadius: 10, overflow: 'hidden' }}>
                    {/* Exercise header */}
                    <div
                      onClick={() => setExpandedExercise(isExpanded ? null : ex.exerciseId)}
                      style={{
                        padding: '12px 16px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        cursor: 'pointer',
                        backgroundColor: isExpanded ? '#F7FAFC' : '#fff',
                        transition: 'background-color 0.15s',
                      }}
                    >
                      <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: blockColor, flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: 14, color: '#2D3748' }}>
                          {ex.exerciseName}
                        </div>
                        <div style={{ fontSize: 12, color: '#718096', marginTop: 2 }}>
                          {ex.method !== EXERCISE_METHOD.STANDARD && (
                            <span style={{ marginRight: 8, padding: '1px 6px', borderRadius: 4, backgroundColor: '#EBF4FF', color: '#2B6CB0', fontSize: 11, fontWeight: 600 }}>
                              {EXERCISE_METHOD_LABELS[ex.method]}
                            </span>
                          )}
                          {formatPrescription(ex)}
                          {ex.isUnilateral && ' (unilaterale)'}
                        </div>
                      </div>
                      <span style={{ fontSize: 14, color: '#A0AEC0', transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
                        ▼
                      </span>
                    </div>

                    {/* Expanded set fields */}
                    {isExpanded && (
                      <div style={{ padding: '0 16px 16px' }}>
                        {/* Table header */}
                        <div style={{ display: 'grid', gridTemplateColumns: ex.isUnilateral ? '50px 1fr 1fr 1fr 70px' : '50px 1fr 1fr 70px', gap: 8, padding: '8px 0 4px', borderBottom: '1px solid #EDF2F7' }}>
                          <div style={colHeaderStyle}>Set</div>
                          {ex.isUnilateral && <div style={colHeaderStyle}>Lato</div>}
                          <div style={colHeaderStyle}>Carico</div>
                          <div style={colHeaderStyle}>Reps</div>
                          <div style={colHeaderStyle}>RPE</div>
                        </div>

                        {/* Set rows */}
                        {ex.sets.map((set, idx) => (
                          <div
                            key={`${set.setNumber}-${set.laterality || idx}`}
                            style={{
                              display: 'grid',
                              gridTemplateColumns: ex.isUnilateral ? '50px 1fr 1fr 1fr 70px' : '50px 1fr 1fr 70px',
                              gap: 8,
                              padding: '8px 0',
                              borderBottom: idx < ex.sets.length - 1 ? '1px solid #F7FAFC' : 'none',
                              alignItems: 'center',
                            }}
                          >
                            <div style={{ fontSize: 13, fontWeight: 600, color: '#718096', textAlign: 'center' }}>
                              {set.setNumber}
                            </div>

                            {ex.isUnilateral && (
                              <div style={{ fontSize: 12, fontWeight: 600, color: set.laterality === 'dx' ? '#3182CE' : '#D69E2E', textAlign: 'center' }}>
                                {set.laterality === 'dx' ? 'DX' : 'SX'}
                              </div>
                            )}

                            {/* Weight input */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                              <input
                                type="number"
                                value={set.weight || ''}
                                onChange={(e) => updateSet(ex.exerciseId, idx, 'weight', parseFloat(e.target.value) || 0)}
                                placeholder="0"
                                style={inputStyle}
                                min={0}
                                step={0.5}
                              />
                              <span style={{ fontSize: 11, color: '#A0AEC0', whiteSpace: 'nowrap' }}>
                                {LOAD_UNIT_LABELS[set.unit as LOAD_UNIT] || set.unit || 'kg'}
                              </span>
                            </div>

                            {/* Reps input */}
                            <input
                              type="number"
                              value={set.repsActual || ''}
                              onChange={(e) => updateSet(ex.exerciseId, idx, 'repsActual', parseInt(e.target.value, 10) || 0)}
                              placeholder="0"
                              style={inputStyle}
                              min={0}
                            />

                            {/* RPE input */}
                            <input
                              type="number"
                              value={set.rpe ?? ''}
                              onChange={(e) => {
                                const val = e.target.value === '' ? null : Math.min(10, Math.max(0, parseFloat(e.target.value)));
                                updateSet(ex.exerciseId, idx, 'rpe', val);
                              }}
                              placeholder="—"
                              style={{ ...inputStyle, width: '100%' }}
                              min={0}
                              max={10}
                              step={0.5}
                            />
                          </div>
                        ))}

                        {/* Notes */}
                        <div style={{ marginTop: 8 }}>
                          <textarea
                            value={ex.notes}
                            onChange={(e) => updateNotes(ex.exerciseId, e.target.value)}
                            placeholder="Note esercizio..."
                            rows={2}
                            style={textareaStyle}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Global RPE */}
          <div style={{ marginTop: 20, padding: '16px', border: '1px solid #E2E8F0', borderRadius: 10, backgroundColor: '#F7FAFC' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ fontWeight: 600, fontSize: 14, color: '#2D3748' }}>RPE Sessione</span>
              <input
                type="number"
                value={globalRpe ?? ''}
                onChange={(e) => {
                  const val = e.target.value === '' ? null : Math.min(10, Math.max(0, parseFloat(e.target.value)));
                  setGlobalRpe(val);
                }}
                placeholder="1-10"
                style={{ ...inputStyle, width: 80, textAlign: 'center' }}
                min={0}
                max={10}
                step={0.5}
              />
            </label>
          </div>

          {/* Compiled by info */}
          <div style={{ marginTop: 12, fontSize: 12, color: '#A0AEC0', display: 'flex', alignItems: 'center', gap: 6 }}>
            <span>Compilato dal coach</span>
            <span style={{ fontWeight: 600, color: '#718096' }}>
              {user?.firstName} {user?.lastName}
            </span>
            <span style={{ marginLeft: 'auto', fontStyle: 'italic' }}>
              compiledBy: {coachId.slice(-8) || '—'}
            </span>
          </div>

          {/* Error */}
          {error && (
            <div style={{ marginTop: 12, padding: '10px 14px', borderRadius: 8, backgroundColor: '#FFF5F5', border: '1px solid #FED7D7', color: '#C53030', fontSize: 13 }}>
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={footerStyle}>
          <button onClick={onClose} style={cancelButtonStyle} disabled={saving}>
            Annulla
          </button>
          <button onClick={handleSave} style={saveButtonStyle} disabled={saving || exercises.length === 0}>
            {saving ? 'Salvataggio...' : existingLog ? 'Aggiorna compilazione' : 'Salva compilazione'}
          </button>
        </div>
      </div>
    </div>
  );
};

// --- Styles ---

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
  padding: 24,
};

const modalStyle: React.CSSProperties = {
  backgroundColor: 'var(--c-card-bg)',
  borderRadius: 16,
  width: '100%',
  maxWidth: 720,
  maxHeight: '90vh',
  display: 'flex',
  flexDirection: 'column',
  boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
};

const headerStyle: React.CSSProperties = {
  padding: '20px 24px',
  borderBottom: '1px solid #E2E8F0',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  flexShrink: 0,
};

const bodyStyle: React.CSSProperties = {
  padding: '20px 24px',
  overflowY: 'auto',
  flex: 1,
};

const footerStyle: React.CSSProperties = {
  padding: '16px 24px',
  borderTop: '1px solid #E2E8F0',
  display: 'flex',
  justifyContent: 'flex-end',
  gap: 12,
  flexShrink: 0,
};

const closeButtonStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  fontSize: 24,
  color: '#A0AEC0',
  padding: '0 4px',
  lineHeight: 1,
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '6px 10px',
  border: '1px solid #E2E8F0',
  borderRadius: 6,
  fontSize: 13,
  color: '#2D3748',
  outline: 'none',
  backgroundColor: 'var(--c-card-bg)',
  transition: 'border-color 0.15s',
};

const textareaStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  border: '1px solid #E2E8F0',
  borderRadius: 6,
  fontSize: 13,
  color: '#2D3748',
  outline: 'none',
  resize: 'vertical',
  fontFamily: 'inherit',
};

const colHeaderStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  color: '#A0AEC0',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
};

const cancelButtonStyle: React.CSSProperties = {
  padding: '10px 20px',
  borderRadius: 8,
  border: '1px solid #E2E8F0',
  backgroundColor: 'var(--c-card-bg)',
  color: '#4A5568',
  fontSize: 14,
  fontWeight: 500,
  cursor: 'pointer',
};

const saveButtonStyle: React.CSSProperties = {
  padding: '10px 24px',
  borderRadius: 8,
  border: 'none',
  backgroundColor: '#3182CE',
  color: '#fff',
  fontSize: 14,
  fontWeight: 600,
  cursor: 'pointer',
  opacity: 1,
};

export default CoachCompileModal;
